import { useLocation, useNavigate, matchPath } from 'react-router-dom';

/**
 * Provides a constant and functions that update a given query parameter within the URL.
 * @param {string} name Name of the URL parameter.
 * @example useURLParam('modal')
 */
export const useUrlParam = (name: string, shouldReplace = true) => {
  const location = useLocation();
  const navigate = useNavigate();

  const getParams = (): URLSearchParams => new URLSearchParams(location.search);

  const getParam = (): string | null => getParams().get(name);

  const matchParamPath = (path: string) => {
    const param = getParam();

    if (typeof param === 'string') {
      return matchPath({ path, end: false }, `/${param}`);
    }
    return null;
  };

  const makeLocation = (params: URLSearchParams) => ({
    ...location,
    search: params.toString(),
  });

  const updateUrl = (params: URLSearchParams) => {
    const prevParams = getParams().toString();
    if (prevParams !== params.toString()) {
      if (shouldReplace) {
        navigate(makeLocation(params), { replace: true });
      } else {
        navigate(makeLocation(params));
      }
    }
  };

  const linkToParam = (path: string) => {
    const params = getParams();
    params.set(name, path);
    return makeLocation(params);
  };

  const setParam = (path: string) => {
    if (!path) return;
    const params = getParams();
    params.set(name, path);
    updateUrl(params);
  };

  const removeParam = () => {
    const params = getParams();
    params.delete(name);
    updateUrl(params);
  };

  return {
    urlParam: getParam(),
    matchParamPath,
    setParam,
    removeParam,
    linkToParam,
  };
};
